<template>
    <transition name="hic-fade">
        <div
            v-if="visible"
            class="hic-overlay"
            :class="{ 'hic-in-dashboard': isInDashboard }"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hic-title"
        >
            <div class="hic-card" role="document">
                <h3 id="hic-title" class="hic-title">Before you continue</h3>

                <p class="hic-body">
                    By continuing, you agree that we may securely use your health information to coordinate your treatment with licensed providers and pharmacies, in accordance with our
                    <a
                        :href="privacyUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="hic-link"
                    >Privacy Policy</a>.
                </p>

                <button
                    type="button"
                    class="hic-agree"
                    @click="agree"
                >
                    I Agree &amp; Continue
                </button>
            </div>
        </div>
    </transition>
</template>

<script>
import { healthConsentBus, resolveHealthConsent } from '@/utils/healthConsentGate'

export default {
    name: 'HealthInfoConsentModal',

    data() {
        return {
            visible: false,
            previousBodyOverflow: '',
            previousBodyPaddingRight: ''
        }
    },

    computed: {
        privacyUrl() {
            const base = process.env.VUE_APP_HOMEPAGE || 'https://rxpros.com/';
            return `${base}/privacy`;
        },
        isInDashboard() {
            return this.$route && typeof this.$route.path === 'string'
                && this.$route.path.indexOf('/home') === 0;
        },
    },

    created() {
        healthConsentBus.$on('hic-open', this.handleOpen)
        healthConsentBus.$on('hic-close', this.handleClose)
    },

    beforeDestroy() {
        healthConsentBus.$off('hic-open', this.handleOpen)
        healthConsentBus.$off('hic-close', this.handleClose)
        this.unlockBodyScroll()
    },

    methods: {
        handleOpen() {
            this.visible = true
            this.lockBodyScroll()
        },

        handleClose() {
            this.visible = false
            this.unlockBodyScroll()
        },

        agree() {
            resolveHealthConsent(true)
        },

        lockBodyScroll() {
            if (typeof document === 'undefined') return
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
            this.previousBodyOverflow = document.body.style.overflow
            this.previousBodyPaddingRight = document.body.style.paddingRight
            document.body.style.overflow = 'hidden'
            if (scrollbarWidth > 0) {
                document.body.style.paddingRight = `${scrollbarWidth}px`
            }
        },

        unlockBodyScroll() {
            if (typeof document === 'undefined') return
            document.body.style.overflow = this.previousBodyOverflow
            document.body.style.paddingRight = this.previousBodyPaddingRight
        }
    }
}
</script>

<style scoped>
.hic-overlay {
    position: fixed;
    inset: 0;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2147483647;
    padding: 16px;
    -webkit-font-smoothing: antialiased;
    font-family: 'Funnel Sans', Inter, system-ui, -apple-system, sans-serif;
}

.hic-card {
    position: relative;
    background: #ffffff;
    color: #111827;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.25);
    width: 100%;
    max-width: 460px;
    padding: 32px 28px 24px;
    box-sizing: border-box;
}

.hic-title {
    font-family: 'Funnel Display', 'Funnel Sans', Inter, sans-serif;
    font-size: 22px;
    line-height: 1.25;
    font-weight: 600;
    color: #111827;
    margin: 0 0 14px;
}

.hic-body {
    font-size: 15px;
    line-height: 1.55;
    color: #374151;
    margin: 0 0 24px;
}

.hic-link {
    color: #52b2cf;
    text-decoration: underline;
    font-weight: 500;
}

.hic-link:hover {
    color: #30989d;
}

.hic-agree {
    display: block;
    width: 100%;
    background: #52b2cf;
    color: #ffffff;
    border: 0;
    border-radius: 10px;
    padding: 14px 20px;
    font-family: inherit;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background-color 0.15s ease, transform 0.05s ease;
}

.hic-agree:hover {
    background: #30989d;
}

.hic-agree:active {
    transform: translateY(1px);
    outline: none;
    box-shadow: none;
}

.hic-agree:focus,
.hic-agree:focus-visible {
    outline: none;
    box-shadow: none;
}

/* Dashboard dark mode — only applies on /home/* with data-theme="dark" */
[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-card {
    background: #181818;
    color: #f5f5f5;
    border-color: #2e2e2e;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.6);
}

[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-title {
    color: #f5f5f5;
}

[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-body {
    color: #d4d4d4;
}

[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-link {
    color: #c9e8ff;
}

[data-theme="dark"] .hic-overlay.hic-in-dashboard .hic-link:hover {
    color: #ffffff;
}

/* Fade/scale transition */
.hic-fade-enter-active,
.hic-fade-leave-active {
    transition: opacity 0.18s ease;
}

.hic-fade-enter-active .hic-card,
.hic-fade-leave-active .hic-card {
    transition: transform 0.22s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.18s ease;
}

.hic-fade-enter,
.hic-fade-leave-to {
    opacity: 0;
}

.hic-fade-enter .hic-card,
.hic-fade-leave-to .hic-card {
    transform: translateY(12px);
    opacity: 0;
}

/* Mobile bottom-sheet layout */
@media (max-width: 640px) {
    .hic-overlay {
        align-items: flex-end;
        padding: 0;
    }

    .hic-card {
        max-width: 100%;
        border-radius: 18px 18px 0 0;
        padding: 28px 20px 24px;
        padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
    }

    .hic-title {
        font-size: 20px;
    }

    .hic-body {
        font-size: 14.5px;
    }

    .hic-fade-enter .hic-card,
    .hic-fade-leave-to .hic-card {
        transform: translateY(100%);
    }
}
</style>
